import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Components
import GenericTable from 'src/components/table/GenericTable';
import { GENERIC_TABLE_DESIGN } from 'src/components/table/GenericTable.design';
import Iconify from 'src/components/Iconify';
import MktEmptyState from 'src/sections/@dashboard/marketing/components/MktEmptyState';
import MktLoading from 'src/sections/@dashboard/marketing/components/MktLoading';
import PlatformChip from 'src/sections/@dashboard/marketing/components/PlatformChip';
import StatusChip from 'src/sections/@dashboard/marketing/components/StatusChip';

// Shadcn Components
import { Button } from 'src/@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';
import { Calendar } from 'src/@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from 'src/@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'src/@/components/ui/dialog';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { MultiSelect } from 'src/@/components/ui/multi-select';
import { toast } from 'src/@/components/ui/use-toast';
import { CalendarIcon, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Utils
import { fDateTimeSuffix } from 'src/utils/formatTime';

// Hooks
import useAuth from 'src/hooks/useAuth';
import useTabs from 'src/hooks/useTabs';

// Actions
import actionListaCanaisPorCliente from 'src/actions/clientes/actionListaCanaisPorCliente';
import v2_actionAnunciosObservados from 'src/actions/v2/marketing/v2_actionAnunciosObservados';
import v2_actionCriarCampanhaRastreamento from 'src/actions/v2/marketing/v2_actionCriarCampanhaRastreamento';
import v2_actionAtualizarCampanhaRastreamento from 'src/actions/v2/marketing/v2_actionAtualizarCampanhaRastreamento';
import v2_actionCampanhasRastreamento from 'src/actions/v2/marketing/v2_actionCampanhasRastreamento';
import v2_actionExcluirCampanhaRastreamento from 'src/actions/v2/marketing/v2_actionExcluirCampanhaRastreamento';

import { RASTREAMENTO_DESIGN as d, getStatusMeta, getCaptureMeta } from './MarketingRastreamento.design';

// ----------------------------------------------------------------------

// Janela e teto da descoberta de anuncios: o multiselect e um atalho pros
// anuncios que estao rodando agora, nao um historico completo.
const ANUNCIOS_DIAS = 90;
const ANUNCIOS_LIMITE = 50;

// Fabrica, e nao constante de modulo: as datas precisam ser resolvidas na hora
// de abrir o formulario. Como constante elas congelariam no carregamento do
// bundle e uma aba aberta por dias criaria campanha com data de ontem.
function criarFormularioVazio() {
  return {
    nome: '',
    plataforma: '',
    orcamento: '',
    dataInicio: new Date(),
    dataFim: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 dias a partir de hoje
    descricao: '',
    objetivo: '',
    // Captura Click-to-WhatsApp: canal que recebe o lead e, opcionalmente, os
    // anuncios a que a campanha se restringe.
    canalId: '',
    ads: [],
    // Configurações avançadas
    palavrasChave: [],
    novaPalavraChave: { type: 'contain', value: '' },
  };
}

export default function MarketingRastreamento() {
  const { t } = useTranslation('marketing');
  const { user, cliente } = useAuth();
  const navigate = useNavigate();

  const statusOptions = useMemo(() => [
    { value: 'all', label: t('tracking.filters.status.all') },
    { value: 'ativa', label: t('tracking.filters.status.active') },
    { value: 'pausada', label: t('tracking.filters.status.paused') },
    { value: 'finalizada', label: t('tracking.filters.status.finished') },
    { value: 'erro', label: t('tracking.filters.status.error') },
  ], [t]);

  const platformOptions = useMemo(() => [
    { value: 'all', label: t('tracking.filters.platform.all') },
    { value: 'facebook', label: 'Facebook Ads' },
    { value: 'google', label: 'Google Ads' },
    { value: 'instagram', label: 'Instagram Ads' },
  ], [t]);

  const headLabels = useMemo(() => [
    { id: 'nome', label: t('tracking.table.campaign'), className: `${d.table.cols.nome} text-xs font-semibold text-muted-foreground uppercase tracking-wider` },
    { id: 'plataforma', label: t('tracking.table.platform'), className: `${d.table.cols.plataforma} text-xs font-semibold text-muted-foreground uppercase tracking-wider` },
    { id: 'status', label: t('tracking.table.status'), className: `${d.table.cols.status} text-xs font-semibold text-muted-foreground uppercase tracking-wider` },
    { id: 'leads', label: t('tracking.table.leads'), className: `${d.table.cols.leads} text-xs font-semibold text-muted-foreground uppercase tracking-wider` },
    { id: 'captura', label: t('tracking.table.capture'), className: `${d.table.cols.captura} text-xs font-semibold text-muted-foreground uppercase tracking-wider` },
    { id: 'inicio', label: t('tracking.table.start'), className: `${d.table.cols.inicio} text-xs font-semibold text-muted-foreground uppercase tracking-wider` },
    { id: 'fim', label: t('tracking.table.end'), className: `${d.table.cols.fim} text-xs font-semibold text-muted-foreground uppercase tracking-wider` },
    { id: 'acoes', label: '', className: d.table.cols.acoes },
  ], [t]);

  // Estados
  const [tableData, setTableData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);

  // Estados para filtros
  const [clienteIdPadrao, setClienteIdPadrao] = useState(user.cliente_id_padrao);
  const [dataInicio, setDataInicio] = useState(new Date(2024, 0, 1));
  const [dataFinal, setDataFinal] = useState(new Date(new Date().getFullYear(), 11, 31));
  const [filtroPlataforma, setFiltroPlataforma] = useState(null);

  // Estados para modal de criação/edição de campanha
  const [modalAberto, setModalAberto] = useState(false);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [campanhaEditando, setCampanhaEditando] = useState(null);
  const [salvandoCampanha, setSalvandoCampanha] = useState(false);

  // Estados para modal de exclusão
  const [modalExclusaoAberto, setModalExclusaoAberto] = useState(false);
  const [campanhaExcluindo, setCampanhaExcluindo] = useState(null);
  const [excluindoCampanha, setExcluindoCampanha] = useState(false);
  const [formularioCampanha, setFormularioCampanha] = useState(criarFormularioVazio);

  // Estados da captura Click-to-WhatsApp
  const [canaisWhatsapp, setCanaisWhatsapp] = useState([]);
  const [anunciosObservados, setAnunciosObservados] = useState([]);
  // Canal a que a descoberta ja respondeu. Sem isso o aviso de "nenhum anuncio"
  // pisca no frame entre escolher o canal e a resposta chegar.
  const [anunciosCanal, setAnunciosCanal] = useState('');
  // Sem isso a secao fica com um vazio sem explicacao entre a troca de canal e
  // a resposta da API — parece quebrado em vez de estar so carregando.
  const [carregandoAnuncios, setCarregandoAnuncios] = useState(false);
  const [novoAnuncioManual, setNovoAnuncioManual] = useState('');
  // O MultiSelect guarda a selecao internamente a partir de `defaultValue` e
  // nunca re-sincroniza. Este contador muda so quando a lista e reescrita por
  // fora (abrir modal, trocar de canal, colar ID manual) e forca a remontagem.
  const [seedAnuncios, setSeedAnuncios] = useState(0);
  // Modo de captura do formulario: anuncio (Click-to-WhatsApp) ou
  // palavra-chave — mutuamente exclusivos, nunca os dois ao mesmo tempo.
  const [modoCaptura, setModoCaptura] = useState('anuncio');

  const { currentTab: filterStatus, onChangeTab: onChangeFilterStatus } = useTabs(null);

  // Buscar dados iniciais
  useEffect(() => {
    if (user.cliente_id_padrao) {
      setClienteIdPadrao(user.cliente_id_padrao);
    }
    buscarCanaisWhatsapp();
    buscarCampanhas(cliente._id);
  }, []);

  // Buscar campanhas quando as datas de filtro mudarem
  useEffect(() => {
    if (dataInicio && dataFinal) {
      buscarCampanhas(cliente._id);
    }
  }, [dataInicio, dataFinal]);

  // Descoberta de anuncios: so com o modal aberto e canal escolhido, pra nao
  // gastar request enquanto o usuario apenas olha a listagem.
  useEffect(() => {
    if (!modalAberto || !formularioCampanha.canalId) {
      setAnunciosObservados([]);
      setAnunciosCanal('');
      setCarregandoAnuncios(false);
      return;
    }
    buscarAnunciosObservados(formularioCampanha.canalId);
  }, [modalAberto, formularioCampanha.canalId]);

  // Funções
  // So canal whatsapp_business recebe o bloco `referral` do Click-to-WhatsApp;
  // o canal 'whatsapp' legado nunca traz esse bloco, entao fica de fora.
  const buscarCanaisWhatsapp = async () => {
    try {
      const response = await actionListaCanaisPorCliente(cliente._id);
      const todosCanais = response.data?.data || [];
      setCanaisWhatsapp(todosCanais.filter((canal) => canal.platform === 'whatsapp_business'));
    } catch (err) {
      toast({ title: t('common.error'), description: t('tracking.toast.channelsFailed'), variant: 'destructive' });
    }
  };

  const buscarAnunciosObservados = async (canalId) => {
    try {
      setCarregandoAnuncios(true);
      const response = await v2_actionAnunciosObservados(cliente._id, {
        canal_id: canalId,
        dias: ANUNCIOS_DIAS,
        limit: ANUNCIOS_LIMITE,
      });
      setAnunciosObservados(response.data?.data || []);
    } catch (error) {
      // Falha na descoberta nao bloqueia a campanha: cai no caminho manual,
      // onde o usuario cola o ID vindo do Gerenciador de Anuncios.
      setAnunciosObservados([]);
    } finally {
      setCarregandoAnuncios(false);
      setAnunciosCanal(canalId);
    }
  };

  const buscarCampanhas = async (clienteId) => {
    try {
      setLoading(true);

      // Formatar datas para o formato esperado pela API
      const dataInicioFormatada = dataInicio ? dataInicio.toISOString().split('T')[0] : null;
      const dataFinalFormatada = dataFinal ? dataFinal.toISOString().split('T')[0] : null;

      // Construir query string com os parâmetros
      let queryParams = '';
      if (dataInicioFormatada && dataFinalFormatada) {
        queryParams = `?dataInicio=${dataInicioFormatada}&dataFinal=${dataFinalFormatada}`;
      }

      // Chamar a action para buscar campanhas reais
      const response = await v2_actionCampanhasRastreamento(clienteId, queryParams);

      if (response.data && response.data.error === false) {
        // Mapear dados da API para o formato esperado pelo componente
        const campanhasMapeadas = response.data.data.map((campanha) => ({
          _id: campanha._id,
          nome: campanha.name,
          plataforma: campanha.platform,
          status: campanha.status === 'active' ? 'ativa' : campanha.status,
          orcamento: parseFloat(campanha.config.budget) || 0,
          gasto: parseFloat(campanha.config.spent) || 0,
          impressoes: parseInt(campanha.config.impressions) || 0,
          cliques: parseInt(campanha.config.clicks) || 0,
          ctr: campanha.config.ctr || 0,
          cpc: parseFloat(campanha.config.cpc) || 0,
          conversoes: campanha.config.conversoes || 0,
          cpa: campanha.config.cpa || 0,
          roas: campanha.config.roas || 0,
          // Garantir datas válidas mesmo se a API não enviar start_date
          data_inicio: new Date(campanha.start_date || campanha.create_at || campanha.update_at),
          data_fim: new Date(campanha.end_date || campanha.update_at || campanha.create_at),
          criado_por: campanha.created_by,
          create_at: campanha.create_at,
          update_at: campanha.update_at,
          // Configurações importantes para edição
          objetivo: campanha.config.type || '',
          palavrasChave: campanha.config.keys || [],
          // config cru: o save preserva o que a tela nao edita em vez de
          // reconstruir o objeto e zerar metricas gravadas por fora.
          config: campanha.config || {},
          // Captura Click-to-WhatsApp. canal_id pode vir cru ou populado,
          // dependendo de quem serializou a campanha — a tela so quer o id.
          canal_id: campanha.canal_id ? String(campanha.canal_id._id || campanha.canal_id) : null,
          ads: campanha.ads || [],
          capturas: campanha.capturas || 0,
        }));

        setTableData(campanhasMapeadas);
      } else {
        setTableData([]);
      }
    } catch (error) {
      setTableData([]);
      toast({ title: t('common.error'), description: t('tracking.toast.loadFailed'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleVerDetalhes = (campanhaId, campanhaNome) => {
    // Navegar para a página de métricas com o ID da campanha
    navigate(`/dashboard/marketing/metricas?campanha=${campanhaId}&nome=${encodeURIComponent(campanhaNome)}`);
  };

  // Função para abrir modal de edição
  const handleEditarCampanha = (campanha) => {
    setModoEdicao(true);
    setCampanhaEditando(campanha);

    // Preencher formulário com dados da campanha. Canal e anuncios entram aqui
    // tambem: sem hidratar, salvar a edicao apagaria a captura ja configurada.
    setFormularioCampanha({
      ...criarFormularioVazio(),
      nome: campanha.nome,
      plataforma: campanha.plataforma,
      orcamento: campanha.orcamento.toString(),
      dataInicio: new Date(campanha.data_inicio),
      dataFim: new Date(campanha.data_fim),
      objetivo: campanha.objetivo || '',
      canalId: campanha.canal_id || '',
      ads: campanha.ads || [],
      palavrasChave: campanha.palavrasChave || [],
    });

    // Modo deriva do canal ja salvo na campanha: com canal, ela captura por
    // anuncio; sem canal, so pode ter sido configurada por palavra-chave.
    setModoCaptura(campanha.canal_id ? 'anuncio' : 'palavra-chave');
    setNovoAnuncioManual('');
    setSeedAnuncios((prev) => prev + 1);
    setModalAberto(true);
  };

  // Função para abrir modal de criação. Zerar modo/campanha ANTES de abrir e o
  // que impede o caso classico: fechar a edicao pelo X e clicar em "Nova
  // campanha" — o modal reabria em modo edicao e o save sobrescrevia a anterior.
  const handleAbrirModalCriacao = () => {
    setModoEdicao(false);
    setCampanhaEditando(null);
    setFormularioCampanha(criarFormularioVazio());
    // Anuncio e o caminho funcional hoje; palavra-chave e legado.
    setModoCaptura('anuncio');
    setNovoAnuncioManual('');
    setSeedAnuncios((prev) => prev + 1);
    setModalAberto(true);
  };

  // Função para fechar modal e resetar estados
  const handleFecharModal = () => {
    setModalAberto(false);
    setModoEdicao(false);
    setCampanhaEditando(null);
    setSalvandoCampanha(false);
    setNovoAnuncioManual('');
    setFormularioCampanha(criarFormularioVazio());
    setModoCaptura('anuncio');
  };

  // Funções para exclusão de campanha
  const handleAbrirModalExclusao = (campanha) => {
    setCampanhaExcluindo(campanha);
    setModalExclusaoAberto(true);
  };

  const handleFecharModalExclusao = () => {
    setModalExclusaoAberto(false);
    setCampanhaExcluindo(null);
    setExcluindoCampanha(false);
  };

  const handleExcluirCampanha = async () => {
    if (!campanhaExcluindo) return;

    try {
      setExcluindoCampanha(true);

      const response = await v2_actionExcluirCampanhaRastreamento(
        cliente._id,
        campanhaExcluindo._id
      );

      if (response.data && response.data.error === false) {
        // Recarregar a lista de campanhas
        await buscarCampanhas(cliente._id);
        handleFecharModalExclusao();
        toast({ title: t('tracking.toast.deleted') });
      } else {
        throw new Error(response.data?.mensagem || t('tracking.toast.deleteFailed'));
      }
    } catch (error) {
      toast({ title: t('tracking.toast.deleteFailed'), description: t('tracking.toast.tryAgain'), variant: 'destructive' });
    } finally {
      setExcluindoCampanha(false);
    }
  };

  // Funções para o modal de criação de campanha
  const handleInputChange = (campo, valor) => {
    setFormularioCampanha((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  // Funções para gerenciar palavras-chave
  const handleAdicionarPalavraChave = () => {
    if (formularioCampanha.novaPalavraChave.value.trim()) {
      setFormularioCampanha((prev) => ({
        ...prev,
        palavrasChave: [...prev.palavrasChave, prev.novaPalavraChave],
        novaPalavraChave: { type: 'contain', value: '' },
      }));
    }
  };

  const handleRemoverPalavraChave = (index) => {
    setFormularioCampanha((prev) => ({
      ...prev,
      palavrasChave: prev.palavrasChave.filter((_, i) => i !== index),
    }));
  };

  const handleTipoPalavraChave = (tipo) => {
    setFormularioCampanha((prev) => ({
      ...prev,
      novaPalavraChave: { ...prev.novaPalavraChave, type: tipo },
    }));
  };

  // Funções para a captura Click-to-WhatsApp
  // Trocar de canal invalida os anuncios escolhidos: cada ad_id pertence a
  // conta de anuncios ligada aquele canal.
  const handleCanalChange = (canalId) => {
    setFormularioCampanha((prev) => ({ ...prev, canalId, ads: [] }));
    setNovoAnuncioManual('');
    setSeedAnuncios((prev) => prev + 1);
  };

  const handleAdicionarAnuncioManual = () => {
    const adId = novoAnuncioManual.trim();
    if (!adId || formularioCampanha.ads.includes(adId)) return;

    setFormularioCampanha((prev) => ({ ...prev, ads: [...prev.ads, adId] }));
    setNovoAnuncioManual('');
    setSeedAnuncios((prev) => prev + 1);
  };

  // Une a descoberta com os IDs ja salvos na campanha: um anuncio que parou de
  // gerar lead sai da agregacao e sumiria da selecao ao editar.
  const opcoesAnuncios = useMemo(() => {
    const observados = anunciosObservados.map((anuncio) => ({
      value: anuncio.ad_id,
      label: anuncio.ad_name ? `${anuncio.ad_name} (${anuncio.total})` : anuncio.ad_id,
    }));
    const conhecidos = new Set(observados.map((opcao) => opcao.value));

    return [
      ...observados,
      ...formularioCampanha.ads
        .filter((adId) => !conhecidos.has(adId))
        .map((adId) => ({ value: adId, label: adId })),
    ];
  }, [anunciosObservados, formularioCampanha.ads]);

  const handleSalvarCampanha = async () => {
    try {
      setSalvandoCampanha(true);

      // Validação básica
      if (!formularioCampanha.nome || !formularioCampanha.plataforma) {
        toast({ title: t('tracking.toast.requiredFields'), variant: 'destructive' });
        return;
      }

      // Ad ID sem canal nao captura nada: o match do CTWA parte do canal que
      // recebeu a mensagem e so depois filtra pelo source_id do referral.
      if (formularioCampanha.ads.length > 0 && !formularioCampanha.canalId) {
        toast({ title: t('tracking.toast.channelRequired'), variant: 'destructive' });
        return;
      }

      // Na edicao o config anterior e a base: reconstruir do zero apagaria
      // spent/impressions/clicks/cpc e qualquer chave gravada por fora da tela.
      const configAnterior = modoEdicao
        ? (campanhaEditando.config || {})
        : { spent: '0', impressions: '0', clicks: '0', cpc: '0' };

      // Preparar dados para a API no formato correto
      const dadosCampanha = {
        name: formularioCampanha.nome,
        platform: formularioCampanha.plataforma,
        start_date: formularioCampanha.dataInicio,
        end_date: formularioCampanha.dataFim,
        created_by: user.id,
        status: 'active',
        // canal_id e ads ficam no TOPO do documento, nunca em config: sao os
        // campos indexados que o servico de captura consulta a cada lead CTWA.
        // Exclusividade mutua reforcada aqui (defesa em profundidade — o
        // backend garante o mesmo): o modo escolhido no toggle decide qual
        // conjunto de campos viaja no payload, o outro sempre vai vazio,
        // mesmo que o usuario tenha preenchido algo antes de trocar de modo.
        canal_id: modoCaptura === 'anuncio' ? (formularioCampanha.canalId || null) : null,
        ads: modoCaptura === 'anuncio' ? formularioCampanha.ads : [],
        config: {
          ...configAnterior,
          type: formularioCampanha.objetivo,
          budget: formularioCampanha.orcamento.toString() || '0',
          keys: modoCaptura === 'palavra-chave' ? formularioCampanha.palavrasChave : [],
        },
        deleted: false,
      };

      const data = { scope: dadosCampanha };

      let response;

      if (modoEdicao) {
        response = await v2_actionAtualizarCampanhaRastreamento(
          cliente._id,
          campanhaEditando._id,
          data
        );
      } else {
        // Chamar a action para criar a campanha
        response = await v2_actionCriarCampanhaRastreamento(
          cliente._id,
          data
        );
      }

      // Verificar se a resposta é válida
      if (!response || !response.data) {
        throw new Error(t('tracking.toast.noApiResponse'));
      }

      // Verificar diferentes formatos de resposta da API
      const isSuccess = response.data.success === true
        || response.data.error === false
        || (response.data.data && !response.data.error);

      if (isSuccess) {
        try {
          // Recarregar a lista de campanhas
          await buscarCampanhas(cliente._id);
        } catch (fetchError) {
          // campanha foi salva mesmo que o recarregamento tenha falhado
        }

        // Fechar modal e limpar formulário SEMPRE em caso de sucesso
        handleFecharModal();
        toast({ title: modoEdicao ? t('tracking.toast.updated') : t('tracking.toast.created') });
      } else {
        // Erro específico da API
        const errorMessage = response.data.message || response.data.error || t('tracking.toast.unknownApiError');
        throw new Error(`API Error: ${errorMessage}`);
      }
    } catch (error) {
      // Mostrar erro mais específico para o usuário
      let errorMsg = t('tracking.toast.saveFailed', {
        action: modoEdicao ? t('tracking.toast.update') : t('tracking.toast.create'),
      });

      if (error.message.includes('API Error:')) {
        errorMsg += ': ' + error.message.replace('API Error:', '');
      } else if (error.response && error.response.data) {
        errorMsg += `: ${error.response.data.message || error.response.data.error || t('tracking.toast.apiError')}`;
      } else {
        errorMsg += `. ${t('tracking.toast.tryAgain')}`;
      }

      toast({ title: errorMsg, variant: 'destructive' });

      // IMPORTANTE: Não fechar o modal em caso de erro para que o usuário possa corrigir
      // Modal só fecha em caso de sucesso (feito acima)
    } finally {
      setSalvandoCampanha(false);
    }
  };

  // Filtrar dados
  const dataFiltered = applySortFilter({
    tableData,
    filterStatus,
    filtroPlataforma,
    dataInicio,
    dataFinal,
  });

  const isFiltering = Boolean(filterStatus) || Boolean(filtroPlataforma);

  return (
    <div className={d.page.root} style={{ fontFamily: d.page.fontStack }}>
      <div className={d.page.stickyHeader}>
        <div className={d.page.container}>
          <div className={d.page.topbar}>
            <div className={d.page.topbarIdentity}>
              <div className={d.page.iconTile}>
                <Iconify icon="eva:bar-chart-2-outline" width={22} />
              </div>
              <div className="min-w-0">
                <h1 className={d.page.headerTitle}>{t('tracking.title')}</h1>
                <p className={d.page.headerSubtitle}>{t('tracking.subtitle')}</p>
              </div>
            </div>
            <div className={d.page.topbarActions}>
              <button type="button" className={d.page.primaryButton} onClick={handleAbrirModalCriacao}>
                <Plus className="h-4 w-4" />
                {t('tracking.newCampaign')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className={`${d.page.container} pt-4 ${d.page.pageBody}`}>
        <div className={d.page.filterBar}>
          <div className={d.page.filterItem}>
            <span className={d.page.filterLabel}>{t('common.status')}</span>
            <Select value={filterStatus ?? 'all'} onValueChange={(value) => onChangeFilterStatus(null, value === 'all' ? null : value)}>
              <SelectTrigger className={d.page.filterSelect}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-[9999]" position="popper" sideOffset={4}>
                {statusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className={d.page.filterItem}>
            <span className={d.page.filterLabel}>{t('common.platform')}</span>
            <Select value={filtroPlataforma || 'all'} onValueChange={(value) => setFiltroPlataforma(value === 'all' ? null : value)}>
              <SelectTrigger className={d.page.filterSelect}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-[9999]" position="popper" sideOffset={4}>
                {platformOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

      {/* Modal de Criação/Edição */}
      <Dialog open={modalAberto} onOpenChange={(open) => (open ? setModalAberto(true) : handleFecharModal())}>
            <DialogContent className="sm:max-w-[600px] rounded-2xl border-0 shadow-2xl bg-card p-0 overflow-hidden">
              <div className="p-6 pb-4 border-b border-border bg-muted/30">
                <DialogHeader>
                  <DialogTitle className="text-xl font-semibold text-foreground">
                    {modoEdicao ? t('tracking.editCampaign') : t('tracking.createCampaign')}
                  </DialogTitle>
                </DialogHeader>
              </div>

              {/* max-h + scroll: com os campos de captura o rodape saia da tela em 1366x768 */}
              <div className="p-6 grid gap-5 max-h-[70vh] overflow-y-auto">
                <div className="grid gap-2">
                  <Label htmlFor="nome" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('tracking.form.name')}</Label>
                  <Input
                    id="nome"
                    placeholder={t('tracking.form.namePlaceholder')}
                    value={formularioCampanha.nome}
                    onChange={(e) => handleInputChange('nome', e.target.value)}
                    className="rounded-xl"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="plataforma" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('tracking.form.platform')}</Label>
                  <Select value={formularioCampanha.plataforma} onValueChange={(value) => handleInputChange('plataforma', value)}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder={t('tracking.form.selectPlatform')} />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl shadow-xl">
                      <SelectItem value="facebook">Facebook Ads</SelectItem>
                      <SelectItem value="google">Google Ads</SelectItem>
                      <SelectItem value="instagram">Instagram Ads</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Tipo de captura: anuncio (Click-to-WhatsApp) ou palavra-chave.
                    Mutuamente exclusivos — o toggle deixa isso explicito pro
                    usuario em vez de mostrar os dois blocos juntos, sem relacao
                    aparente entre eles (era a fonte da confusao reportada). */}
                <div className="grid gap-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('tracking.form.captureMode')}</Label>
                  <div className={d.captureMode.container}>
                    <button
                      type="button"
                      onClick={() => setModoCaptura('anuncio')}
                      className={modoCaptura === 'anuncio' ? d.captureMode.optionActive : d.captureMode.option}
                    >
                      <Iconify icon="mdi:bullhorn-outline" width={16} />
                      {t('tracking.form.captureModeAd')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setModoCaptura('palavra-chave')}
                      className={modoCaptura === 'palavra-chave' ? d.captureMode.optionActive : d.captureMode.option}
                    >
                      <Iconify icon="mdi:key-outline" width={16} />
                      {t('tracking.form.captureModeKeyword')}
                    </button>
                  </div>
                  <p className={d.form.hint}>{t('tracking.form.captureModeHint')}</p>
                </div>

                {modoCaptura === 'anuncio' && (
                  <>
                    {/* Canal de captura Click-to-WhatsApp */}
                    <div className="grid gap-2">
                      <Label htmlFor="canal" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('tracking.form.channel')}</Label>
                      {canaisWhatsapp.length > 0 ? (
                        <>
                          <Select value={formularioCampanha.canalId} onValueChange={handleCanalChange}>
                            <SelectTrigger id="canal" className="rounded-xl">
                              <SelectValue placeholder={t('tracking.form.selectChannel')} />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl shadow-xl">
                              {canaisWhatsapp.map((canal) => (
                                <SelectItem key={canal._id} value={canal._id}>
                                  {canal.nome || canal.nome_cliente || canal.sessao}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className={d.form.hint}>{t('tracking.form.channelHint')}</p>
                        </>
                      ) : (
                        <div className={d.form.notice}>
                          <p className={d.form.hint}>{t('tracking.form.channelEmpty')}</p>
                        </div>
                      )}
                    </div>

                    {/* Anúncios rastreados — lista vazia captura o canal inteiro */}
                    {formularioCampanha.canalId && (
                      <div className="grid gap-2">
                        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('tracking.form.ads')}</Label>
                        {carregandoAnuncios && (
                          <p className={d.form.hint}>{t('common.loading')}</p>
                        )}
                        {!carregandoAnuncios && opcoesAnuncios.length > 0 && (
                          <MultiSelect
                            key={`anuncios-${seedAnuncios}`}
                            options={opcoesAnuncios}
                            onValueChange={(valores) => handleInputChange('ads', valores)}
                            defaultValue={formularioCampanha.ads}
                            placeholder={t('tracking.form.adsAll')}
                            variant="inverted"
                            maxCount={3}
                            className="rounded-xl"
                          />
                        )}
                        {anunciosCanal === formularioCampanha.canalId && anunciosObservados.length === 0 && (
                          <div className={d.form.notice}>
                            <p className={d.form.hint}>{t('tracking.form.adsEmpty')}</p>
                            <div className="flex gap-2">
                              <Input
                                placeholder={t('tracking.form.adsManual')}
                                value={novoAnuncioManual}
                                onChange={(e) => setNovoAnuncioManual(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleAdicionarAnuncioManual()}
                                className="flex-1 rounded-xl"
                              />
                              <Button
                                type="button"
                                variant="secondary"
                                onClick={handleAdicionarAnuncioManual}
                                disabled={!novoAnuncioManual.trim()}
                                className="rounded-xl"
                              >
                                {t('tracking.form.adsManualAdd')}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* Palavras-chave — movido pra logo abaixo do toggle: era o
                    caminho de captura legado, mostrado junto de Canal+Anuncios
                    sem indicar que eram alternativas, nao complementos. */}
                {modoCaptura === 'palavra-chave' && (
                  <div className="grid gap-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('tracking.form.keywords')}</Label>
                    <div className="border border-border rounded-xl p-4 space-y-4 bg-muted/30">
                      {/* Lista de palavras-chave adicionadas */}
                      {formularioCampanha.palavrasChave.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {formularioCampanha.palavrasChave.map((palavra, index) => (
                            <div key={index} className="flex items-center gap-2 bg-card border border-border pl-3 pr-1 py-1 rounded-lg shadow-sm">
                              <span className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider">
                                {palavra.type === 'exact' ? t('tracking.keywords.exact') : t('tracking.keywords.contains')}
                              </span>
                              <span className="text-sm font-medium text-foreground border-l border-border pl-2">{palavra.value}</span>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleRemoverPalavraChave(index)}
                                className="h-6 w-6 p-0 text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-md"
                              >
                                ×
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Adicionar nova palavra-chave */}
                      <div className="flex gap-2">
                        <Select
                          value={formularioCampanha.novaPalavraChave.type}
                          onValueChange={handleTipoPalavraChave}
                        >
                          <SelectTrigger className="w-28 rounded-xl">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="rounded-xl shadow-xl">
                            <SelectItem value="contain">{t('tracking.keywords.contains')}</SelectItem>
                            <SelectItem value="exact">{t('tracking.keywords.exact')}</SelectItem>
                          </SelectContent>
                        </Select>

                        <div className="flex-1 flex gap-2">
                          <Input
                            placeholder={t('tracking.keywords.placeholder')}
                            value={formularioCampanha.novaPalavraChave.value}
                            onChange={(e) => setFormularioCampanha((prev) => ({
                              ...prev,
                              novaPalavraChave: { ...prev.novaPalavraChave, value: e.target.value },
                            }))}
                            onKeyPress={(e) => e.key === 'Enter' && handleAdicionarPalavraChave()}
                            className="flex-1 rounded-xl"
                          />
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={handleAdicionarPalavraChave}
                            disabled={!formularioCampanha.novaPalavraChave.value.trim()}
                            className="rounded-xl"
                          >
                            {t('tracking.keywords.add')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid gap-2">
                  <Label htmlFor="objetivo" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('tracking.form.objective')}</Label>
                  <Select value={formularioCampanha.objetivo} onValueChange={(value) => handleInputChange('objetivo', value)}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder={t('tracking.form.selectObjective')} />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl shadow-xl">
                      <SelectItem value="conversao">{t('tracking.objectives.conversion')}</SelectItem>
                      <SelectItem value="alcance">{t('tracking.objectives.reach')}</SelectItem>
                      <SelectItem value="engajamento">{t('tracking.objectives.engagement')}</SelectItem>
                      <SelectItem value="trafico">{t('tracking.objectives.traffic')}</SelectItem>
                      <SelectItem value="vendas">{t('tracking.objectives.sales')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="orcamento" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('tracking.form.budget')}</Label>
                  <Input
                    id="orcamento"
                    type="number"
                    placeholder="Ex: 5000"
                    value={formularioCampanha.orcamento}
                    onChange={(e) => handleInputChange('orcamento', e.target.value)}
                    className="rounded-xl"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('tracking.form.startDate')}</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="justify-start text-left font-normal rounded-xl">
                          <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                          {formularioCampanha.dataInicio ? format(formularioCampanha.dataInicio, 'dd/MM/yyyy', { locale: ptBR }) : <span className="text-muted-foreground">{t('common.select')}</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 rounded-xl shadow-xl">
                        <Calendar
                          mode="single"
                          selected={formularioCampanha.dataInicio}
                          onSelect={(date) => handleInputChange('dataInicio', date)}
                          initialFocus
                          className="rounded-xl"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div className="grid gap-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t('tracking.form.endDate')}</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="justify-start text-left font-normal rounded-xl">
                          <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                          {formularioCampanha.dataFim ? format(formularioCampanha.dataFim, 'dd/MM/yyyy', { locale: ptBR }) : <span className="text-muted-foreground">{t('common.select')}</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 rounded-xl shadow-xl">
                        <Calendar
                          mode="single"
                          selected={formularioCampanha.dataFim}
                          onSelect={(date) => handleInputChange('dataFim', date)}
                          initialFocus
                          className="rounded-xl"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

              </div>

              <div className="flex justify-end gap-3 p-6 pt-2 border-t border-border bg-muted/30">
                <Button
                  variant="ghost"
                  onClick={handleFecharModal}
                  disabled={salvandoCampanha}
                  className="rounded-xl"
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  onClick={handleSalvarCampanha}
                  disabled={salvandoCampanha}
                  className="rounded-xl px-6"
                >
                  {salvandoCampanha ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/20 border-t-white" />
                      {modoEdicao ? t('common.saving') : t('common.creating')}
                    </div>
                  ) : (
                    modoEdicao ? t('common.saveChanges') : t('tracking.createAction')
                  )}
                </Button>
              </div>
            </DialogContent>
      </Dialog>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog open={modalExclusaoAberto} onOpenChange={setModalExclusaoAberto}>
        <DialogContent className="sm:max-w-[400px] rounded-2xl border-0 shadow-2xl bg-card p-0 overflow-hidden">
          <div className="p-6 pb-4 border-b border-border bg-red-50/50 dark:bg-red-500/10">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold text-foreground flex items-center gap-2">
                <Iconify icon="mdi:alert-circle-outline" width={24} height={24} className="text-red-500" />
                {t('tracking.delete.title')}
              </DialogTitle>
            </DialogHeader>
          </div>

          <div className="p-6">
            <p className="text-muted-foreground">
              {t('tracking.delete.confirm', { name: campanhaExcluindo?.nome })}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              {t('tracking.delete.warning')}
            </p>
          </div>

          <div className="flex justify-end gap-3 p-6 pt-2 border-t border-border bg-muted/30">
            <Button
              variant="ghost"
              onClick={handleFecharModalExclusao}
              disabled={excluindoCampanha}
              className="rounded-xl"
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleExcluirCampanha}
              disabled={excluindoCampanha}
              className="rounded-xl px-6"
            >
              {excluindoCampanha ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/20 border-t-white" />
                  {t('common.deleting')}
                </div>
              ) : (
                t('tracking.delete.title')
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Loading */}
      {loading && <MktLoading text={t('tracking.loading')} />}

      {/* Tabela */}
      {!loading && dataFiltered.length > 0 && (
        <>
          <GenericTable headLabels={headLabels}>
            {dataFiltered
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row) => {
                const { _id, nome, plataforma, status, data_inicio, data_fim, capturas, canal_id } = row;
                const statusCfg = getStatusMeta(status, t);
                const capturaCfg = getCaptureMeta(Boolean(canal_id), t);

                return (
                  <div key={_id} className={GENERIC_TABLE_DESIGN.row.container}>
                    <div className={`${d.table.cols.nome} flex items-center`}>
                      <span className={`${d.table.cell} font-medium`}>{nome}</span>
                    </div>
                    <div className={`${d.table.cols.plataforma} flex items-center`}>
                      <PlatformChip platform={plataforma} size="sm" />
                    </div>
                    <div className={`${d.table.cols.status} flex items-center`}>
                      <StatusChip {...statusCfg} size="sm" />
                    </div>
                    <div className={`${d.table.cols.leads} flex items-center`}>
                      <span className={`${d.table.cell} font-medium tabular-nums`}>{capturas}</span>
                    </div>
                    <div className={`${d.table.cols.captura} flex items-center`}>
                      <StatusChip {...capturaCfg} size="sm" />
                    </div>
                    <div className={`${d.table.cols.inicio} flex items-center`}>
                      <span className={d.table.cellMuted}>{fDateTimeSuffix(new Date(data_inicio))}</span>
                    </div>
                    <div className={`${d.table.cols.fim} flex items-center`}>
                      <span className={d.table.cellMuted}>{fDateTimeSuffix(new Date(data_fim))}</span>
                    </div>
                    <div className={d.table.cols.acoes} onClick={(e) => e.stopPropagation()}>
                      <button type="button" title={t('common.edit')} onClick={() => handleEditarCampanha(row)} className={d.table.actionBtn}>
                        <Iconify icon="mdi:pencil" width={16} />
                      </button>
                      <button type="button" title={t('common.viewDetails')} onClick={() => handleVerDetalhes(_id, nome)} className={d.table.actionBtn}>
                        <Iconify icon="mdi:eye" width={16} />
                      </button>
                      <button type="button" title={t('common.delete')} onClick={() => handleAbrirModalExclusao(row)} className={d.table.actionBtnDanger}>
                        <Iconify icon="mdi:trash-can-outline" width={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
          </GenericTable>

          {/* Paginação */}
          <div className="flex flex-wrap justify-between items-center gap-3 p-4 mt-3 rounded-xl border border-border bg-card">
            <span className={d.table.pageInfo}>
              {t('common.pagination.showing', {
                from: page * rowsPerPage + 1,
                to: Math.min((page + 1) * rowsPerPage, dataFiltered.length),
                total: dataFiltered.length,
              })}
            </span>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground uppercase font-semibold tracking-wider">{t('common.pagination.rows')}</span>
                <Select value={rowsPerPage.toString()} onValueChange={(value) => handleChangeRowsPerPage({ target: { value } })}>
                  <SelectTrigger className="w-16 h-8 text-xs rounded-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleChangePage(null, Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="h-8 w-8 p-0 rounded-lg"
                >
                  <Iconify icon="mdi:chevron-left" width={20} height={20} />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleChangePage(null, Math.min(Math.ceil(dataFiltered.length / rowsPerPage) - 1, page + 1))}
                  disabled={page >= Math.ceil(dataFiltered.length / rowsPerPage) - 1}
                  className="h-8 w-8 p-0 rounded-lg"
                >
                  <Iconify icon="mdi:chevron-right" width={20} height={20} />
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Empty State */}
      {!loading && dataFiltered.length === 0 && (
        <MktEmptyState
          icon="mdi:magnify-remove-outline"
          title={t('tracking.empty.title')}
          subtitle={isFiltering
            ? t('tracking.empty.filtered')
            : t('tracking.empty.default')}
        />
      )}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------

function applySortFilter({ tableData, filterStatus, filtroPlataforma, dataInicio, dataFinal }) {
  let filtered = tableData;

  // Filtrar por status
  if (filterStatus !== null) {
    filtered = filtered.filter((item) => item.status === filterStatus);
  }

  // Filtrar por plataforma
  if (filtroPlataforma !== null) {
    filtered = filtered.filter((item) => item.plataforma === filtroPlataforma);
  }

  // Filtrar por período
  if (dataInicio && dataFinal) {
    filtered = filtered.filter((item) => {
      const itemDate = new Date(item.data_inicio);
      return itemDate >= dataInicio && itemDate <= dataFinal;
    });
  }

  return filtered;
}
